import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { SearchIcon, StarIcon, UserIcon, ClockIcon, BookOpenIcon, ShieldCheckIcon, ShieldIcon } from 'lucide-react';
import SaveToFavorites from '../../components/UI/SaveToFavorites';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
const ReaderDirectory = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [selectedReaderType, setSelectedReaderType] = useState('all');
  const navigate = useNavigate();
  const location = useLocation();
  // Parse query parameters to set initial reader type filter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const typeParam = params.get('type');
    if (typeParam && ['alpha', 'beta', 'arc'].includes(typeParam)) {
      setSelectedReaderType(typeParam);
    }
  }, [location.search]);
  const readers = [{
    id: 1,
    name: 'Sarah Johnson',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=100&h=100',
    readerType: ['beta', 'arc'],
    genres: ['Romance', 'Contemporary', 'YA'],
    rating: 4.8,
    reviewCount: 156,
    responseTime: '24-48 hours',
    completedReviews: 89,
    availability: 'Available',
    bio: 'Passionate reader with a keen eye for character development and plot consistency. English major with 3 years of beta reading experience.',
    preferences: {
      genres: ['Romance', 'Contemporary', 'YA'],
      excludes: ['Horror', 'Extreme Violence'],
      format: ['Digital', 'Print']
    }
  }, {
    id: 2,
    name: 'Michael Chen',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=100&h=100',
    readerType: ['alpha', 'beta'],
    genres: ['Science Fiction', 'Fantasy', 'Horror'],
    rating: 4.9,
    reviewCount: 203,
    responseTime: '1-2 days',
    completedReviews: 145,
    availability: 'Limited',
    bio: 'Software engineer by day, avid reader by night. Specializes in worldbuilding and magic system consistency.',
    preferences: {
      genres: ['Science Fiction', 'Fantasy', 'Horror'],
      excludes: ['Romance', 'Erotica'],
      format: ['Digital']
    }
  }, {
    id: 3,
    name: 'Emma Williams',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=100&h=100',
    readerType: ['arc'],
    genres: ['Mystery', 'Thriller', 'Crime'],
    rating: 4.7,
    reviewCount: 78,
    responseTime: '3-4 days',
    completedReviews: 45,
    availability: 'Available',
    bio: 'Former editor turned ARC reader. Detailed feedback on plot holes and pacing issues.',
    preferences: {
      genres: ['Mystery', 'Thriller', 'Crime'],
      excludes: ['Graphic Violence', 'Horror'],
      format: ['Digital', 'Print', 'Audio']
    }
  }];
  const genres = [{
    id: 'all',
    name: 'All Genres'
  }, {
    id: 'romance',
    name: 'Romance'
  }, {
    id: 'fantasy',
    name: 'Fantasy'
  }, {
    id: 'scifi',
    name: 'Science Fiction'
  }, {
    id: 'mystery',
    name: 'Mystery'
  }, {
    id: 'thriller',
    name: 'Thriller'
  }, {
    id: 'horror',
    name: 'Horror'
  }, {
    id: 'ya',
    name: 'Young Adult'
  }];
  const readerTypes = [{
    id: 'all',
    name: 'All Types'
  }, {
    id: 'alpha',
    name: 'Alpha Reader'
  }, {
    id: 'beta',
    name: 'Beta Reader'
  }, {
    id: 'arc',
    name: 'ARC Reader'
  }];
  const filteredReaders = readers.filter(reader => {
    const matchesSearch = reader.name.toLowerCase().includes(searchQuery.toLowerCase()) || reader.bio.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesGenre = selectedGenre === 'all' || reader.genres.some(g => g.toLowerCase().includes(selectedGenre));
    const matchesType = selectedReaderType === 'all' || reader.readerType.includes(selectedReaderType);
    return matchesSearch && matchesGenre && matchesType;
  });
  const handleReaderClick = (readerId: number) => {
    navigate(`/services/readers/${readerId}`);
  };
  // Update URL when filters change
  const updateQueryParams = (type: string) => {
    setSelectedReaderType(type);
    if (type === 'all') {
      navigate('/services/readers');
    } else {
      navigate(`/services/readers?type=${type}`);
    }
  };
  return <div className={`min-h-screen ${dk.page}`} data-id="element-13429" data-mp="7piegdfzln9c">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-13430" data-mp="7np9r3oxpd3z">
        <div className="mb-8" data-id="element-13431" data-mp="br3zwn6l79v5">
          <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-13432" data-mp="bsf1cgck85j3">
            Reader Directory
          </h1>
          <p className={`mt-2 text-lg ${dk.textSecondary}`} data-id="element-13433" data-mp="45i3419k08ln">
            Connect with experienced Alpha, Beta, and ARC readers for your
            manuscript
          </p>
        </div>

        {/* Reader Type Tabs */}
        <div className="mb-6 flex flex-wrap gap-2" data-id="element-13434" data-mp="96fsoyww84x6">
          {readerTypes.map(type => <button key={type.id} onClick={() => updateQueryParams(type.id)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${selectedReaderType === type.id ? `text-white ${rt.bgDark}` : `${dk.badgeNeutral} hover:opacity-80`}`} data-id="element-13435" data-mp="94l7djhpmask">
              {type.name}
            </button>)}
        </div>

        {/* Search and Filters */}
        <div className="mb-8 flex flex-col md:flex-row gap-4" data-id="element-13436" data-mp="2yvhtlugn8tre">
          <div className="flex-1 relative" data-id="element-13437" data-mp="2ytwudrjdwmn7">
            <SearchIcon className={`h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 ${dk.textFaint}`} data-id="element-13438" data-mp="2tx6k5ku9gl9p" />
            <input type="text" placeholder="Search readers by name or description..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.input}`} data-id="element-13439" data-mp="3jk0uqf77wnus" />
          </div>
          <select value={selectedGenre} onChange={e => setSelectedGenre(e.target.value)} className={`w-full md:w-48 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.input}`} data-id="element-13440" data-mp="30mg7fck3pxca">
            {genres.map(genre => <option key={genre.id} value={genre.id} data-id="element-13441" data-mp="3em8tgukonpdx">
                {genre.name}
              </option>)}
          </select>
        </div>

        {/* Readers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-13442" data-mp="tvi4cfasfl4k">
          {filteredReaders.map(reader => <div key={reader.id} className={`rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow border ${dk.card}`} onClick={() => handleReaderClick(reader.id)} data-id="element-13443" data-mp="2ee2cbs5ldcad">
              <div className="flex items-start space-x-4" data-id="element-13444" data-mp="24m50cj6regal">
                <img src={reader.avatar} alt={reader.name} className="w-16 h-16 rounded-full object-cover flex-shrink-0" data-id="element-13445" data-mp="1zp68666nojf1" />
                <div className="flex-1 min-w-0" data-id="element-13446" data-mp="1qo82ljgkk3uc">
                  <div className="flex items-start justify-between gap-2" data-id="element-13447" data-mp="1qmn4xkyyk4s9">
                    <div data-id="element-13448" data-mp="1lpwouxn5l96u">
                      <h2 className={`text-lg font-semibold flex items-center gap-1.5 ${dk.textPrimary}`} data-id="element-13449" data-mp="1lp3qol29fs0b">
                        {reader.name}
                        {reader.rating >= 4.7 ? <span className="flex items-center text-xs text-green-600" data-id="element-13450" data-mp="3b39z13hy5rfv">
                            <ShieldCheckIcon className="h-4 w-4 text-green-500 mr-0.5" data-id="element-13451" data-mp="2jyfsw6eofkvh" />
                            Verified
                          </span> : <span className="flex items-center text-xs text-amber-600" data-id="element-13452" data-mp="2ozd94dg4m4uc">
                            <ShieldIcon className="h-4 w-4 text-amber-500 mr-0.5" data-id="element-13453" data-mp="2b41bnyal609q" />
                            Pending
                          </span>}
                      </h2>
                      <div className="flex items-center mt-1" data-id="element-13454" data-mp="21aar34hky9zb">
                        <StarIcon className="h-4 w-4 text-yellow-400 fill-yellow-400" data-id="element-13455" data-mp="267u2zxqt2ao3" />
                        <span className={`ml-1 text-sm ${dk.textSecondary}`} data-id="element-13456" data-mp="1s8b7c4f6krso">
                          {reader.rating} ({reader.reviewCount} reviews)
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0" data-id="element-13457" data-mp="3mm2hr65c3fp6">
                      <span className={`px-3 py-1 ${rt.badgeRadius} text-xs font-medium ${reader.availability === 'Available' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-13458" data-mp="3qll2a0t8torh">
                        {reader.availability}
                      </span>
                      <SaveToFavorites itemId={`reader-${reader.id}`} size="sm" data-id="element-13459" data-mp="1oqn83ortqta4" />
                    </div>
                  </div>

                  <p className={`mt-2 text-sm ${dk.textSecondary}`} data-id="element-13460" data-mp="1yloz6q5nu5fc">
                    {reader.bio}
                  </p>

                  <div className="mt-4 space-y-2" data-id="element-13461" data-mp="27o7vssiurlt5">
                    {[{
                  icon: UserIcon,
                  label: `Reader Type: ${reader.readerType.map(t => t.toUpperCase()).join(', ')}`
                }, {
                  icon: ClockIcon,
                  label: `Response Time: ${reader.responseTime}`
                }, {
                  icon: BookOpenIcon,
                  label: `Completed Reviews: ${reader.completedReviews}`
                }].map(({
                  icon: Icon,
                  label
                }) => <div key={label} className={`flex items-center text-sm ${dk.textSecondary}`} data-id="element-13462" data-mp="15bdjb6ko6vhx">
                        <Icon className={`h-4 w-4 mr-2 flex-shrink-0 ${dk.textFaint}`} data-id="element-13463" data-mp="1x0yjfhvb4i1y" />
                        {label}
                      </div>)}
                  </div>

                  <div className="mt-4" data-id="element-13464" data-mp="1o7lhxrj6g849">
                    <h3 className={`text-sm font-medium mb-2 ${dk.textPrimary}`} data-id="element-13465" data-mp="1jb4smu08rnpl">
                      Preferences:
                    </h3>
                    <div className="flex flex-wrap gap-1.5" data-id="element-13466" data-mp="2bzk6hd7mzzzc">
                      {reader.preferences.genres.map(genre => <span key={genre} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${rt.badge}`} data-id="element-13467" data-mp="2jbgpu5a1p4e6">
                          {genre}
                        </span>)}
                    </div>
                  </div>

                  <div className="mt-4 flex gap-2" data-id="element-13468" data-mp="329awwdrm55tx">
                    <button className={`flex-1 px-4 py-2 rounded-md transition-colors text-sm font-medium text-white ${rt.primaryButton}`} onClick={e => {
                  e.stopPropagation();
                  navigate(`/services/readers/${reader.id}`);
                }} data-id="element-13469" data-mp="328hxxyywbkmc">
                      View Profile
                    </button>
                    <button className={`flex-1 px-4 py-2 border rounded-md transition-colors text-sm font-medium ${rt.outlineButton}`} onClick={e => {
                  e.stopPropagation();
                  alert(`Contact ${reader.name}`);
                }} data-id="element-13470" data-mp="3kxplyvide9s3">
                      Contact
                    </button>
                  </div>
                </div>
              </div>
            </div>)}

          {filteredReaders.length === 0 && <div className={`col-span-full text-center py-12 rounded-xl border-2 border-dashed ${dk.emptyState}`} data-id="element-13471" data-mp="29k3mbssan3ke">
              <p className={`text-lg ${dk.textMuted}`} data-id="element-13472" data-mp="24l0l6jffphhp">
                No readers found matching your criteria.
              </p>
            </div>}
        </div>
      </div>
    </div>;
};
export default ReaderDirectory;